import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cardImageStyle, CardHoverPreview } from "@/components/Card3D";
import { ChatPanel } from "@/components/ChatPanel";
import { ArrowLeft, Coins, Trophy, Tv, Users, Flag, Heart, Flame, MessageSquare, Crown, ShieldAlert } from "lucide-react";

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(t); }, []);
  if (!targetIso) return 0;
  return Math.max(0, Math.ceil((new Date(targetIso).getTime() - now) / 1000));
}

// One racer's odds row + live track lane.
function RacerLane({ r, pool, totalPool, mult, isWinner, betting, selected, onSelect, status }) {
  const share = totalPool > 0 ? Math.round((pool / totalPool) * 100) : 0;
  return (
    <div
      role={betting ? "button" : undefined}
      onClick={betting ? onSelect : undefined}
      data-testid={`spec-racer-${r.idx}`}
      className={`rounded-xl border p-2.5 transition-colors duration-150 ${betting ? "cursor-pointer" : ""} ${selected ? "border-amber-300 bg-amber-300/10" : isWinner ? "border-emerald-400/60 bg-emerald-400/[0.06]" : "border-white/10 bg-black/30 hover:border-white/25"}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="relative h-12 w-9 rounded overflow-hidden bg-black shrink-0" style={{ boxShadow: `0 0 0 1.5px ${r.color}` }}>
          {r.card?.front_image && <img src={r.card.front_image} alt={r.title} className="absolute inset-0 w-full h-full" style={cardImageStyle(r.card)} draggable={false} />}
          {isWinner && <div className="absolute inset-0 bg-emerald-500/25 flex items-center justify-center"><Crown className="h-4 w-4 text-amber-300" /></div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-display text-sm truncate" style={{ color: r.color }}>{r.title}</span>
            <span className="font-mono text-[10px] text-amber-300 inline-flex items-center gap-0.5 shrink-0"><Coins className="h-3 w-3" /> {pool.toLocaleString()}</span>
          </div>
          {/* track / progress */}
          <div className="mt-1 h-2 rounded-full bg-white/10 overflow-hidden relative">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${status === "betting" ? share : r.progress}%`, background: status === "betting" ? r.color : `linear-gradient(90deg, ${r.color}, #fff8)` }} />
          </div>
          <div className="mt-0.5 flex items-center justify-between font-mono text-[9px] text-white/45">
            <span>{status === "betting" ? `${share}% of pool` : `${r.progress}% · ${r.hp_pct}% HP`}</span>
            <span>{mult != null ? `~${mult.toFixed(2)}x` : "no backers"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RaceSpectatorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [lane, setLane] = useState(0);
  const [amount, setAmount] = useState(50);
  const [busy, setBusy] = useState(false);

  const { data: m, error } = useQuery({
    queryKey: ["spec-race", id],
    queryFn: async () => (await api.get(`/race/spectator/${id}`)).data,
    refetchInterval: (q) => (q.state.data?.status === "finished" ? false : 8000),
  });

  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`${API}/race/${id}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => { qc.invalidateQueries({ queryKey: ["spec-race", id] }); };
    return () => es.close();
  }, [id, qc]);

  const closesIn = useCountdown(m?.bet_closes_at);
  const cut = m?.profit_cut_pct ?? 0.35;

  const mults = useMemo(() => {
    if (!m) return {};
    const total = m.total_pool;
    const out = {};
    m.lanes.forEach((_, i) => {
      const p = m.pools[String(i)] || 0;
      out[i] = p > 0 ? 1 + ((total - p) * (1 - cut)) / p : null;
    });
    return out;
  }, [m, cut]);

  const placeBet = async () => {
    setBusy(true);
    try {
      await api.post(`/race/spectator/${id}/bet`, { lane, amount: Number(amount) });
      qc.invalidateQueries({ queryKey: ["spec-race", id] });
      refresh();
      toast.success(`Bet placed on ${m.lanes[lane].title}!`);
    } catch (e) { toast.error(apiError(e, "Couldn't place bet")); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-white/50">Race not found. <button className="text-cyan-300 underline" onClick={() => navigate("/race")}>Back to the Gauntlet</button></div>;
  if (!m) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-white/50">Loading the pit…</div>;

  const betOpen = m.status === "betting" && closesIn > 0;
  const myTotal = m.my_bets.reduce((s, b) => s + b.amount, 0);
  const ranked = m.status === "finished" && m.standings
    ? m.standings.map((idx) => m.lanes[idx])
    : [...m.lanes].sort((a, b) => b.progress - a.progress);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="race-spectator-page">
      <Link to="/race" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm mb-4" data-testid="spec-back">
        <ArrowLeft className="h-4 w-4" /> Gauntlet
      </Link>

      {/* Status banner */}
      <div className="hud-pill rounded-2xl p-4 mb-5 text-center relative overflow-hidden">
        <div className="absolute inset-0 holo-scan opacity-20" />
        <div className="relative">
          {m.status === "betting" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-300 inline-flex items-center gap-1.5"><Tv className="h-3.5 w-3.5" /> Bets Open — AI Gauntlet</div>
              <div className="font-display text-4xl text-white mt-1 tabular-nums" data-testid="spec-countdown">{closesIn}s</div>
              <div className="text-xs text-white/45 mt-0.5">until the racers leave the line</div>
            </>
          )}
          {m.status === "racing" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 inline-flex items-center gap-1.5 animate-pulse"><Flame className="h-3.5 w-3.5" /> Live Race</div>
              <div className="font-display text-2xl text-white mt-1">Bets locked — turn {m.turn}</div>
            </>
          )}
          {m.status === "finished" && (
            <>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">Race Over</div>
              <div className="font-display text-3xl mt-1 text-amber-300" data-testid="spec-winner">
                <Trophy className="inline h-6 w-6 mb-1 mr-1" /> {m.lanes[m.winner_lane]?.title} WINS
              </div>
              {m.my_payout != null && (
                <div className={`mt-1 font-mono text-sm ${m.my_payout > 0 ? "text-emerald-300" : "text-white/40"}`} data-testid="spec-payout">
                  {m.my_payout > 0 ? `You collected ${m.my_payout.toLocaleString()}c!` : "Your bets didn't land this time."}
                </div>
              )}
            </>
          )}
          <div className="mt-2 font-mono text-[11px] text-amber-300 inline-flex items-center gap-1">
            <Coins className="h-3.5 w-3.5" /> {m.total_pool.toLocaleString()}c pot · <Users className="h-3 w-3" /> {m.bettors} bettor{m.bettors === 1 ? "" : "s"} · <Flag className="h-3 w-3" /> {m.distance}m
          </div>
        </div>
      </div>

      {/* Racers — odds while betting, live track while racing */}
      <div className="space-y-2 mb-5" data-testid="spec-track">
        {ranked.map((r) => (
          <CardHoverPreview key={r.idx} card={r.card} side="right" align="start">
            <div>
              <RacerLane
                r={r} status={m.status}
                pool={m.pools[String(r.idx)] || 0} totalPool={m.total_pool} mult={mults[r.idx]}
                isWinner={m.status === "finished" && r.idx === m.winner_lane}
                betting={betOpen} selected={betOpen && lane === r.idx}
                onSelect={() => setLane(r.idx)}
              />
            </div>
          </CardHoverPreview>
        ))}
      </div>

      {/* Betting panel */}
      {betOpen && (
        <div className="hud-pill rounded-2xl p-4 mb-5" data-testid="spec-bet-panel">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Back the winner — pick a racer above</div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-2 rounded-lg border border-amber-300/40 bg-amber-300/10 font-display text-sm shrink-0" style={{ color: m.lanes[lane]?.color }}>
              {m.lanes[lane]?.title}
            </div>
            <div className="relative flex-1">
              <Coins className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300" />
              <Input type="number" min={m.min_bet} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="spec-bet-amount" className="pl-9 bg-[hsl(220_18%_12%)] border-white/10 text-white" />
            </div>
            <Button data-testid="spec-place-bet" disabled={busy || Number(amount) < m.min_bet} onClick={placeBet} className="bg-amber-400 text-black hover:bg-amber-300 shrink-0">
              {busy ? "Placing…" : "Place Bet"}
            </Button>
          </div>
          <div className="mt-1.5 font-mono text-[10px] text-white/40">
            {Math.round((m.fee_pct ?? 0.1) * 100)}% entry fee · {Math.round(amount * (1 - (m.fee_pct ?? 0.1)))}c enters the pool · min {m.min_bet}c · pari-mutuel on 1st place
          </div>
          <div className="mt-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 flex items-start gap-2" data-testid="bet-disclaimer">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-[10px] leading-snug text-amber-200/90">
              You bet against <span className="font-semibold text-amber-100">other players</span>, never the house. Winnings come from the shared pool. If no one backs another racer, there's nothing to win — you simply get your wager back minus the {Math.round((m.fee_pct ?? 0.1) * 100)}% house cut.
            </p>
          </div>
          {(() => {
            const lanesWithPool = Object.values(m.pools || {}).filter((v) => v > 0).length;
            const oneSided = (m.total_pool || 0) > 0 && lanesWithPool < 2;
            if (!oneSided) return null;
            return (
              <div data-testid="spec-race-unopposed-warning" className="mt-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-[11px] leading-snug text-amber-100">
                <span className="font-semibold text-amber-200">Only one racer is backed.</span> If nobody bets on another lane before the race starts, every wager is refunded in full — pari-mutuel can't pay out without a losing pool.
              </div>
            );
          })()}
        </div>
      )}

      {/* My bets */}
      {m.my_bets.length > 0 && (
        <div className="hud-pill rounded-xl p-3 mb-5 flex items-center justify-between" data-testid="spec-my-bets">
          <span className="font-mono text-[11px] text-white/55">Your bets: {m.my_bets.map((b) => `${b.amount}c on ${m.lanes[b.lane]?.title}`).join(" · ")}</span>
          <span className="font-mono text-[11px] text-amber-300">{myTotal.toLocaleString()}c</span>
        </div>
      )}

      {/* Race feed */}
      <div className="hud-pill rounded-xl p-3" data-testid="spec-feed">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mb-1.5">Race Feed</div>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {[...m.log].reverse().map((e) => (
            <div key={e.t} className={`font-mono text-[11px] flex gap-2 ${e.type === "win" || e.type === "finish" ? "text-amber-300" : e.type === "attack" ? "text-orange-300/90" : "text-white/55"}`}>
              <span className="text-white/25">T{e.turn}</span><span>{e.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Spectator chat */}
      <div className="hud-pill rounded-xl p-3 mt-4" data-testid="spec-chat-section">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4 text-cyan-300" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">Pit Chat</span>
        </div>
        <ChatPanel channel={`race:${id}`} testidPrefix="spec-chat" heightClass="h-44" />
      </div>
    </div>
  );
}
